import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import {
  Search, ChevronDown, ChevronUp, HelpCircle, ThumbsUp, ThumbsDown,
  AlertCircle, Send, Loader2, X, MessageSquare, Sparkles,
} from "lucide-react";
import { apiClient } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { CARTAL_CONTACT, CARTAL_LABELS } from "../lib/contactInfo";
import { FEATURES } from "../lib/features";
import { Mail, Phone } from "lucide-react";
import Seo from "../components/Seo";

export default function FaqPage() {
  const { lang } = useI18n();
  const { category: catParam } = useParams();
  const [search, setSearch] = useSearchParams();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCat, setActiveCat] = useState(catParam || "");
  const [query, setQuery] = useState(search.get("q") || "");
  const [expanded, setExpanded] = useState(null);
  const [escFor, setEscFor] = useState(null); // {article_id, type}

  useEffect(() => {
    apiClient.get("/faq/categories").then(({ data }) => setCategories(data.items || []));
  }, []);

  // Debounced search
  useEffect(() => {
    setLoading(true);
    const params = {};
    if (activeCat) params.category = activeCat;
    if (query.trim()) params.q = query.trim();
    const t = setTimeout(() => {
      apiClient.get("/faq", { params })
        .then(({ data }) => setItems(data.items || []))
        .catch(() => setItems([]))
        .finally(() => setLoading(false));
    }, 280);
    return () => clearTimeout(t);
  }, [activeCat, query]);

  // Sync URL query string
  useEffect(() => {
    const next = new URLSearchParams();
    if (query) next.set("q", query);
    setSearch(next, { replace: true });
  }, [query, setSearch]);

  const setCat = (key) => {
    setActiveCat(key);
    if (key) navigate(`/faq/${key}${query ? `?q=${encodeURIComponent(query)}` : ""}`);
    else navigate(`/faq${query ? `?q=${encodeURIComponent(query)}` : ""}`);
  };

  return (
    <div className="max-w-[1300px] mx-auto px-5 sm:px-8 lg:px-12 py-10" data-testid="faq-page">
      <Seo
        title={lang === "ar" ? "الأسئلة الشائعة | كارتال" : "Frequently Asked Questions | CARTAL"}
        description={lang === "ar" ? "إجابات أسئلة كارتال الشائعة — البيع والشراء والدفع والشحن والفحص وضمانات المنصة." : "Answers to the most common CARTAL questions — buying, selling, payments, delivery, inspection and platform guarantees."}
        canonicalPath="/faq"
      />
      <div className="overline overline-gold mb-2 inline-flex items-center gap-2">
        <Sparkles className="w-3 h-3" /> {lang === "ar" ? "مركز المساعدة" : "Help Center"}
      </div>
      <h1 className="font-display text-4xl sm:text-5xl tracking-tighter font-medium">
        {lang === "ar" ? "الأسئلة الشائعة" : "Frequently Asked Questions"}
      </h1>
      <p className="text-[#525252] mt-3 max-w-2xl">
        {lang === "ar"
          ? (FEATURES.ESCROW_ENABLED
              ? "كل ما تحتاج معرفته عن الشراء، البيع، التمويل، التأمين، الفحص، الضمان، والمزيد."
              : "كل ما تحتاج معرفته عن الشراء، البيع، التمويل، التأمين، الفحص، والمزيد.")
          : (FEATURES.ESCROW_ENABLED
              ? "Everything you need to know about buying, selling, financing, insurance, inspections, escrow and more."
              : "Everything you need to know about buying, selling, financing, insurance, inspections and more.")}
      </p>

      {/* ── Need help? — official support channels (additive) ── */}
      <div
        className="mt-6 rounded-3xl border-2 p-5 sm:p-6"
        style={{
          background: "linear-gradient(135deg,rgba(201,165,92,0.08),rgba(10,10,10,0.03))",
          borderColor: "rgba(201,165,92,0.4)",
        }}
        data-testid="faq-need-help-card"
      >
        <div className="flex items-start gap-4">
          <div
            className="w-11 h-11 rounded-full flex items-center justify-center shrink-0"
            style={{ background: "linear-gradient(135deg,#c9a55c,#8a6f2e)" }}
          >
            <HelpCircle className="w-5 h-5" style={{ color: "#0a0a0a" }} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#8a6f2e]">
              {lang === "ar" ? "هل تحتاج مساعدة؟" : "Need help?"}
            </div>
            <h2 className="font-display text-lg sm:text-xl font-medium tracking-tight text-[#0a0a0a] mt-0.5">
              {lang === "ar" ? "تواصل مع دعم كارتال مباشرة" : "Reach CARTAL support directly"}
            </h2>
            <p className="text-sm text-[#525252] mt-1 leading-relaxed">
              {lang === "ar"
                ? "فريقنا متاح للرد على استفساراتك عبر واتساب، البريد، أو الرقم الموحد."
                : "Our team is here to help via WhatsApp, email, or our national unified number."}
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              <a
                href={CARTAL_CONTACT.whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-bold transition-colors"
                style={{ backgroundColor: "#25D366", color: "#0a0a0a" }}
                data-testid="faq-help-whatsapp"
              >
                <MessageSquare className="w-4 h-4" />
                {lang === "ar" ? CARTAL_LABELS.whatsapp.ar : CARTAL_LABELS.whatsapp.en}
              </a>
              <a
                href={CARTAL_CONTACT.supportEmailMailto}
                className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-bold border-2 transition-colors"
                style={{ borderColor: "#c9a55c", color: "#8a6f2e" }}
                data-testid="faq-help-email"
              >
                <Mail className="w-4 h-4" />
                {CARTAL_CONTACT.supportEmail}
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="mt-6 max-w-2xl">
        <div className="flex items-center gap-2 bg-white border-2 border-[#0a0a0a]/10 rounded-2xl p-1.5 focus-within:border-[#c9a55c] focus-within:shadow-[0_0_0_4px_rgba(201,165,92,0.18)] transition-all">
          <div className="pl-3 pr-1 text-[#8a8a8a]"><Search className="w-5 h-5" /></div>
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={lang === "ar" ? "ابحث عن سؤالك..." : "Search your question..."}
            className="flex-1 px-3 py-3 outline-none text-[15px] bg-transparent"
            data-testid="faq-search-input"
          />
          {query && (
            <button onClick={() => setQuery("")} className="px-3 text-[#8a8a8a] hover:text-[#0a0a0a]" data-testid="faq-search-clear">
              <X className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>

      <div className="grid lg:grid-cols-[260px_1fr] gap-8 mt-8">
        {/* Categories */}
        <aside className="space-y-1" data-testid="faq-categories">
          <button
            onClick={() => setCat("")}
            className={`w-full text-start px-3 py-2 rounded-lg text-sm transition-colors ${activeCat === "" ? "bg-[#0a0a0a] text-white" : "hover:bg-[#0a0a0a]/5 text-[#3a3a3a]"}`}
            data-testid="faq-cat-all"
          >
            {lang === "ar" ? "جميع الفئات" : "All categories"}
          </button>
          {categories.map((c) => (
            <button
              key={c.key}
              onClick={() => setCat(c.key)}
              className={`w-full text-start px-3 py-2 rounded-lg text-sm transition-colors ${activeCat === c.key ? "bg-[#0a0a0a] text-white" : "hover:bg-[#0a0a0a]/5 text-[#3a3a3a]"}`}
              data-testid={`faq-cat-${c.key}`}
            >
              {lang === "ar" ? c.ar : c.en}
            </button>
          ))}
        </aside>

        {/* Articles */}
        <div className="space-y-3" data-testid="faq-articles">
          {loading ? (
            <div className="text-sm text-[#8a8a8a] inline-flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              {lang === "ar" ? "جارٍ البحث..." : "Searching..."}
            </div>
          ) : items.length === 0 ? (
            <EmptyState lang={lang} query={query} onEscalate={() => setEscFor({ type: "question" })} />
          ) : (
            items.map((a) => (
              <FaqAccordion
                key={a.id}
                article={a}
                lang={lang}
                expanded={expanded === a.id}
                onToggle={() => setExpanded(expanded === a.id ? null : a.id)}
                onEscalate={(type) => setEscFor({ article_id: a.id, type })}
              />
            ))
          )}
        </div>
      </div>

      {escFor && <EscalationModal lang={lang} ctx={escFor} onClose={() => setEscFor(null)} />}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
function FaqAccordion({ article, lang, expanded, onToggle, onEscalate }) {
  const q = lang === "ar" ? article.question_ar : article.question_en;
  const [voted, setVoted] = useState(null);
  const [related, setRelated] = useState(article.related || []);
  const [fullAnswer, setFullAnswer] = useState(null);

  const ensureFull = async () => {
    if (fullAnswer) return;
    try {
      const { data } = await apiClient.get(`/faq/${article.id}`);
      setFullAnswer(lang === "ar" ? data.answer_ar : data.answer_en);
      setRelated(data.related || []);
    } catch {
      setFullAnswer(lang === "ar" ? article.answer_ar : article.answer_en);
    }
  };

  const handleToggle = () => {
    if (!expanded) ensureFull();
    onToggle();
  };

  const vote = async (helpful) => {
    if (voted !== null) return;
    setVoted(helpful);
    try { await apiClient.post(`/faq/${article.id}/helpful`, { helpful }); } catch { /* ignore */ }
  };

  const answer = fullAnswer ?? (lang === "ar" ? article.answer_ar : article.answer_en);

  return (
    <div className="surface rounded-2xl overflow-hidden transition-shadow hover:shadow-md" data-testid={`faq-item-${article.id}`}>
      <button
        type="button"
        onClick={handleToggle}
        className="w-full text-start px-5 py-4 flex items-center gap-4"
        aria-expanded={expanded}
        data-testid={`faq-toggle-${article.id}`}
      >
        <HelpCircle className="w-5 h-5 text-[#8a6f2e] shrink-0" />
        <div className="flex-1 font-display text-base sm:text-lg">{q}</div>
        {expanded ? <ChevronUp className="w-5 h-5 text-[#8a8a8a]" /> : <ChevronDown className="w-5 h-5 text-[#8a8a8a]" />}
      </button>
      {expanded && (
        <div className="px-5 pb-5 pt-1 text-[15px] leading-relaxed text-[#3a3a3a] space-y-4" data-testid={`faq-content-${article.id}`}>
          <p className="whitespace-pre-line">{answer}</p>
          {related && related.length > 0 && (
            <div className="border-t border-[#0a0a0a]/8 pt-3">
              <div className="text-xs text-[#8a8a8a] font-semibold uppercase tracking-widest mb-2">
                {lang === "ar" ? "أسئلة ذات صلة" : "Related questions"}
              </div>
              <div className="flex flex-wrap gap-2">
                {related.map((r) => (
                  <Link
                    key={r.id}
                    to={`/faq/${r.category}#${r.id}`}
                    className="text-xs px-3 py-1.5 rounded-full bg-[#0a0a0a]/5 hover:bg-[#0a0a0a]/10 text-[#3a3a3a] transition-colors"
                    data-testid={`faq-related-${r.id}`}
                  >
                    {lang === "ar" ? r.question_ar : r.question_en}
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Helpful voting */}
          <div className="flex items-center justify-between flex-wrap gap-3 border-t border-[#0a0a0a]/8 pt-3">
            <div className="text-sm text-[#525252]">
              {lang === "ar" ? "هل كانت الإجابة مفيدة؟" : "Was this helpful?"}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => vote(true)}
                disabled={voted !== null}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm transition-colors ${voted === true ? "bg-green-100 text-green-700" : "border border-[#0a0a0a]/15 hover:bg-[#0a0a0a]/5"}`}
                data-testid={`faq-vote-yes-${article.id}`}
              >
                <ThumbsUp className="w-4 h-4" />
                {lang === "ar" ? "نعم" : "Yes"}
              </button>
              <button
                onClick={() => vote(false)}
                disabled={voted !== null}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm transition-colors ${voted === false ? "bg-rose-100 text-rose-700" : "border border-[#0a0a0a]/15 hover:bg-[#0a0a0a]/5"}`}
                data-testid={`faq-vote-no-${article.id}`}
              >
                <ThumbsDown className="w-4 h-4" />
                {lang === "ar" ? "لا" : "No"}
              </button>
            </div>
          </div>

          {voted === false && (
            <div className="bg-[#fff7e6] border border-[#c9a55c]/40 rounded-xl p-4 space-y-3" data-testid={`faq-still-need-${article.id}`}>
              <div className="text-sm font-semibold text-[#3a3a3a]">
                {lang === "ar" ? "ما زلت بحاجة لمساعدة؟" : "Still need help?"}
              </div>
              <div className="flex flex-wrap gap-2">
                <button onClick={() => onEscalate("suggestion")} className="text-xs px-3 py-1.5 rounded-full bg-[#0a0a0a] text-white hover:bg-[#3a3a3a]" data-testid={`faq-submit-suggestion-${article.id}`}>
                  {lang === "ar" ? "أرسل اقتراح" : "Submit suggestion"}
                </button>
                <button onClick={() => onEscalate("complaint")} className="text-xs px-3 py-1.5 rounded-full bg-[#0a0a0a] text-white hover:bg-[#3a3a3a]" data-testid={`faq-submit-complaint-${article.id}`}>
                  {lang === "ar" ? "أرسل شكوى" : "Submit complaint"}
                </button>
                <button onClick={() => onEscalate("question")} className="text-xs px-3 py-1.5 rounded-full bg-[#c9a55c] text-[#0a0a0a] hover:bg-[#d4b56e]" data-testid={`faq-contact-admin-${article.id}`}>
                  <MessageSquare className="w-3 h-3 inline" />
                  {" "}
                  {lang === "ar" ? "تواصل مع الإدارة" : "Contact administrator"}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
function EmptyState({ lang, query, onEscalate }) {
  return (
    <div className="surface rounded-2xl p-8 text-center" data-testid="faq-empty">
      <AlertCircle className="w-12 h-12 mx-auto text-[#c9a55c] mb-3" />
      <h3 className="font-display text-xl mb-1">
        {lang === "ar" ? "لم نجد أي نتائج" : "No results found"}
      </h3>
      <p className="text-sm text-[#525252] max-w-md mx-auto">
        {lang === "ar"
          ? `لم نجد أي مقال يطابق "${query}". جرّب كلمات أخرى أو أرسل سؤالك للإدارة وسنضيف إجابة قريباً.`
          : `No article matches "${query}". Try different words or ask the administrator — we'll add an answer soon.`}
      </p>
      <button onClick={onEscalate} className="btn-primary mt-5" data-testid="faq-empty-cta">
        <Send className="w-4 h-4" />
        {lang === "ar" ? "أرسل سؤالك" : "Submit your question"}
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
function EscalationModal({ lang, ctx, onClose }) {
  const [form, setForm] = useState({ subject: "", message: "", email: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  const labels = {
    question: lang === "ar" ? "تواصل مع الإدارة" : "Contact administrator",
    suggestion: lang === "ar" ? "إرسال اقتراح" : "Submit a suggestion",
    complaint: lang === "ar" ? "تقديم شكوى" : "Submit a complaint",
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.subject.trim() || !form.message.trim()) {
      setErr(lang === "ar" ? "العنوان والرسالة مطلوبان" : "Subject and message required");
      return;
    }
    setBusy(true);
    setErr("");
    try {
      await apiClient.post("/faq/escalate", {
        type: ctx.type,
        article_id: ctx.article_id,
        subject: form.subject,
        message: form.message,
        email: form.email || undefined,
      });
      setDone(true);
    } catch (e) {
      setErr(e?.response?.data?.detail || (lang === "ar" ? "حدث خطأ" : "Error"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="faq-escalation-modal">
      <div className="bg-white rounded-3xl w-full max-w-lg p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-2xl">{labels[ctx.type]}</h3>
          <button onClick={onClose} className="w-9 h-9 rounded-full hover:bg-[#0a0a0a]/5 flex items-center justify-center" data-testid="faq-escalation-close">
            <X className="w-5 h-5" />
          </button>
        </div>
        {done ? (
          <div className="text-center py-6" data-testid="faq-escalation-success">
            <div className="w-14 h-14 mx-auto rounded-full bg-green-100 text-green-700 flex items-center justify-center text-3xl">✓</div>
            <h4 className="font-display text-xl mt-3">{lang === "ar" ? "وصلتنا رسالتك" : "Your message has been received"}</h4>
            <p className="text-sm text-[#525252] mt-2">
              {lang === "ar" ? "سيقوم فريق الإدارة بالمراجعة والرد قريباً." : "Our administrator team will review and respond shortly."}
            </p>
            <button onClick={onClose} className="btn-primary mt-5">{lang === "ar" ? "إغلاق" : "Close"}</button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3" data-testid="faq-escalation-form">
            <input
              type="text"
              placeholder={lang === "ar" ? "العنوان (مثال: مشكلة في تسجيل الدخول)" : "Subject (e.g. login issue)"}
              value={form.subject}
              onChange={(e) => setForm({ ...form, subject: e.target.value })}
              className="w-full px-4 py-3 border-2 border-[#0a0a0a]/10 rounded-xl text-sm focus:border-[#c9a55c] outline-none"
              data-testid="faq-esc-subject"
              required
            />
            <textarea
              placeholder={lang === "ar" ? "اكتب رسالتك..." : "Write your message..."}
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              rows={5}
              className="w-full px-4 py-3 border-2 border-[#0a0a0a]/10 rounded-xl text-sm focus:border-[#c9a55c] outline-none resize-y"
              data-testid="faq-esc-message"
              required
            />
            <input
              type="email"
              placeholder={lang === "ar" ? "بريدك الإلكتروني (اختياري)" : "Your email (optional)"}
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full px-4 py-3 border-2 border-[#0a0a0a]/10 rounded-xl text-sm focus:border-[#c9a55c] outline-none"
              data-testid="faq-esc-email"
            />
            {err && <div className="text-sm text-rose-600">{err}</div>}
            <button type="submit" disabled={busy} className="btn-primary w-full justify-center" data-testid="faq-esc-submit">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {lang === "ar" ? "إرسال" : "Send"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
